/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Vers.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, JSX } from 'preact';
import { usePress } from '../UNSAFE_usePress';
import { usePressClick } from '../UNSAFE_usePressClick';
import { useTabbableMode } from '../UNSAFE_useTabbableMode';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import type { UnpackSignals } from '../../utils/UNSAFE_attributeUtils';
import { processGlobals } from '../../utils/UNSAFE_buttonUtils';
import type {
  ButtonIntrinsicProps,
  ButtonIntrinsicAriaProps
} from '../../utils/UNSAFE_buttonUtils';
import { useTestId, TestIdProps } from '../UNSAFE_useTestId';

/**
 * The ActionDetail reasons
 */
type ActionDetail = {
  reason: 'pointer' | 'keyboard';
};

/**
 * Element details
 */
export type BareButtonElementDetails =
  | {
      type: 'button';
      isFocusable?: never;
      buttonType?: 'button' | 'submit';
      isLabel?: never;
      isSwitch?: boolean;
    }
  | {
      type: 'a';
      buttonType?: never;
      href: string;
      target?: string;
      rel?: string;
      isFocusable?: never;
      isLabel?: never;
      isSwitch?: never;
    }
  | {
      type: 'span';
      buttonType?: never;
      isFocusable?: boolean;
      isLabel?: boolean;
      isSwitch?: boolean;
    }
  | {
      type: 'div';
      buttonType?: never;
      isFocusable?: boolean;
      isLabel?: boolean;
      isSwitch?: boolean;
    };

export type BareButtonProps = ButtonIntrinsicProps &
  ButtonIntrinsicAriaProps &
  TestIdProps & {
    /**
     *The default slot is the button's text label. The oj-c-base-button element accepts plain text or DOM nodes as children for the default slot."
     */
    children?: ComponentChildren;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifies if repeated events are dispatched for key or pointer held down
     */
    isRepeat?: boolean;

    /**
     * Specifies if Button should use only a click handler, not keyup/down, ptr up/down
     */
    isClick?: boolean;

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: (details: ActionDetail) => void;

    /**
     * Indicate element type and associated details
     */
    elementDetails?: BareButtonElementDetails;
  };

const roleProps = (
  elType: string,
  hidden: boolean,
  isFocusable: boolean,
  isLabel: boolean,
  isSwitch: boolean
): { role?: UnpackSignals<JSX.HTMLAttributes['role']> } => {
  switch (elType) {
    case 'link':
      return { role: 'link' };
    case 'button':
      return isSwitch ? { role: 'switch' } : {};
    case 'div':
      return hidden ? {} : { role: 'button' };
    case 'span':
      return isLabel
        ? {}
        : hidden
        ? { role: 'none' }
        : isFocusable
        ? isSwitch
          ? { role: 'switch' }
          : { role: 'button' }
        : { role: 'img' };
    default:
      return hidden ? { role: 'none' } : isFocusable ? { role: 'button' } : { role: 'img' };
  }
};

/**
 * BareButtons are unformatted bare buttons that direct users to initiate or take actions and work with a single tap, click, or keystroke.
 */
export function useBareButton({
  onAction,
  isDisabled,
  isRepeat,
  isClick,
  elementDetails = { type: 'button', buttonType: 'button' },
  testId,
  ...props
}: BareButtonProps): Record<string, any> {
  const clientHints = getClientHints();
  // :active only works on IOS devices if a touch handler exists
  const iosProps = clientHints.platform === 'ios' ? { ontouchstart: function () {} } : {};

  // pressHandler can be invoked by both usePress and usePressClick
  // usePress is used for elements that do not generate a click for kb interaction (non-buttons)
  // usePressClick is used for elements that generate a click for kb interaction (buttons)
  // So e could be a PointerEvent or KeyboardEvent

  const pressHandler = (e: Event) => {
    if (onAction) {
      onAction({
        reason: (e as PointerEvent).pointerType == '' ? 'keyboard' : 'pointer'
      });
    }
  };
  const {
    globalEventProps,
    ariaProps: { 'aria-disabled': ariaDisabled, 'aria-hidden': ariaHidden, ...ariaOtherProps }
  } = processGlobals(props);

  const { pressProps } = usePress(pressHandler, {
    isDisabled: isDisabled,
    isRepeat
  });

  const { pressProps: pressClickProps } = usePressClick(pressHandler, {
    isDisabled: isDisabled
  });

  const {
    type: elementType = 'button',
    isFocusable: isElementFocusable = true,
    isLabel: isElementLabel = false,
    isSwitch: isElementSwitch = false,
    buttonType: elementButtonType,
    ...elementProps
  } = { ...elementDetails };

  const buttonType = elementDetails.type === 'button' ? elementButtonType ?? 'button' : undefined;
  const typeProps =
    elementDetails.type === 'button' && elementButtonType === 'button' ? { type: buttonType } : {};

  // 'role' should be 'none' when aria-hidden is true or 'img' when false and not focusable
  const role = roleProps(
    elementType,
    Boolean(ariaHidden),
    isElementFocusable ?? false,
    isElementLabel ?? false,
    isElementSwitch ?? false
  );
  const ElementType = elementDetails?.type;
  const isSpan = ElementType == 'span';
  const isBareButton = ElementType == 'button';
  const { tabbableModeProps } = useTabbableMode(isDisabled);

  const arDisabled =
    ElementType == 'button'
      ? ariaDisabled
        ? { 'aria-disabled': ariaDisabled }
        : {}
      : isDisabled
      ? { 'aria-disabled': isDisabled }
      : {};

  const arHidden = ElementType === 'div' ? {} : { 'aria-hidden': ariaHidden };

  const adjustedAriaProps =
    role.role === 'none' ? {} : mergeProps(arDisabled, arHidden, ariaOtherProps);

  const testIdProps = useTestId(testId);

  const getTabIndex = () => {
    if ((isSpan && (isElementFocusable === false || isDisabled)) || (isBareButton && isDisabled)) {
      // no tabIndex value should be defined for disabled button elements and non-focusable or disabled span elements
      return { tabIndex: undefined };
    }
    return tabbableModeProps;
  };

  const legacyGlobals = {
    autofocus: props.autofocus,
    title: props.title,
    disabled: isDisabled
  };

  return mergeProps(
    elementProps,
    onAction ? (isRepeat || (isClick ?? isBareButton) ? pressClickProps : pressProps) : {},
    iosProps,
    globalEventProps,
    adjustedAriaProps,
    getTabIndex(),
    typeProps,
    testIdProps,
    role,
    legacyGlobals
  );
}
